import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { AuthField } from "@/components/AuthField";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { CADGridBackground } from "@/components/CADGridBackground";
import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useAuthStore } from "@/store/authStore";
import { useOnboardingStore } from "@/store/onboardingStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.08, duration: 0.6, ease: EASE },
  }),
};

function LockGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
      <rect x="2.5" y="7" width="11" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="8" cy="10.75" r="0.9" fill="currentColor" />
    </svg>
  );
}

export default function OnboardingPersonalScreen() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setPersonal = useOnboardingStore((s) => s.setPersonal);
  const [fullName, setFullName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<{ fullName?: string; phone?: string }>({});
  const [leaving, setLeaving] = useState(false);

  const go = () => {
    setLeaving(true);
    window.setTimeout(() => navigate("/onboarding/verification"), 320);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (fullName.trim().length < 2) next.fullName = "Enter your full name";
    if (phone.replace(/\D/g, "").length < 7) next.phone = "Enter a valid phone number";
    setErrors(next);
    if (Object.keys(next).length) return;
    setPersonal({ fullName: fullName.trim(), phone: phone.trim() });
    go();
  };

  return (
    <div
      data-testid="onboarding-personal-screen"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6 py-12"
    >
      <CADGridBackground opacity={0.28} />
      <div
        aria-hidden
        className="absolute left-1/2 top-[40%] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.05] blur-3xl"
      />

      <AnimatePresence>
        {!leaving && (
          <motion.form
            onSubmit={onSubmit}
            noValidate
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, x: -24, transition: { duration: 0.3, ease: "easeIn" } }}
            className="relative flex w-full max-w-sm flex-col gap-7"
          >
            <motion.div variants={item} custom={0} className="flex flex-col gap-6">
              <BuildEaseLogo height={36} />
              <OnboardingProgress step={1} total={2} />
            </motion.div>

            <div className="flex flex-col gap-2">
              <div className="overflow-hidden">
                <motion.h1
                  data-testid="onboarding-personal-headline"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
                  className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
                >
                  Tell us about you
                </motion.h1>
              </div>
              <motion.p
                variants={item}
                custom={2}
                data-testid="onboarding-personal-subtext"
                className="font-sans text-sm text-white/50"
              >
                This helps us personalize your experience.
              </motion.p>
            </div>

            <div className="flex flex-col gap-4">
              <motion.div variants={item} custom={3}>
                <AuthField
                  label="Full Name"
                  index="01"
                  testId="onboarding-name-input"
                  type="text"
                  autoComplete="name"
                  placeholder="Amara Okafor"
                  value={fullName}
                  error={errors.fullName}
                  onChange={(e) => { setFullName(e.target.value); setErrors((p) => ({ ...p, fullName: undefined })); }}
                />
              </motion.div>
              <motion.div variants={item} custom={4}>
                <AuthField
                  label="Phone Number"
                  index="02"
                  testId="onboarding-phone-input"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+27 82 000 0000"
                  value={phone}
                  error={errors.phone}
                  onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: undefined })); }}
                />
              </motion.div>
              <motion.p
                variants={item}
                custom={5}
                data-testid="onboarding-reassurance"
                className="flex items-center gap-2 px-1 font-sans text-xs text-cad"
              >
                <LockGlyph />
                Your information is encrypted and never shared.
              </motion.p>
            </div>

            <motion.div variants={item} custom={6} className="flex flex-col items-center gap-5">
              <motion.button
                type="submit"
                data-testid="onboarding-continue-button"
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 24 }}
                className="group relative flex h-14 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-safety font-display text-base font-semibold text-white shadow-[0_12px_40px_-12px_rgba(255,91,0,0.7)]"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
                <span className="relative flex items-center gap-2">
                  Continue
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </motion.button>
              <Link
                to="/onboarding/verification"
                data-testid="onboarding-skip-link"
                onClick={(e) => { e.preventDefault(); go(); }}
                className="font-sans text-sm text-cad transition-colors duration-200 hover:text-white"
              >
                Skip for now
              </Link>
            </motion.div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
